/**
 * VisualEngine. Owns three.js ping-pong feedback + post pass. Never touches
 * AudioContext or PointerEvents. Constructor builds everything synchronously;
 * loadMode() is async (VideoSource.ready). render() is the only method that
 * calls renderer.render() — apply() only updates uniforms.
 */

import * as THREE from 'three';
import type { VisualParams, AudioLevels, TouchStamp, ModeConfig } from '../types';
import { TouchTexture } from '../visual/touch-texture';
import { VideoSource } from '../visual/video-source';

// Shader sources imported as raw strings via Vite's ?raw suffix (built-in, no config needed).
import feedbackVert from '../shaders/feedback.vert.glsl?raw';
import feedbackFrag from '../shaders/feedback.frag.glsl?raw';
import postVert from '../shaders/post.vert.glsl?raw';
import postFrag from '../shaders/post.frag.glsl?raw';

// ─── Constants ─────────────────────────────────────────────────────────────

/** Render target scale relative to device pixel ratio per spec §4.3. */
const RT_SCALE = 0.5;

/** Hard cap for each feedback render target. Keeps mobile GPU memory bounded. */
const MAX_RT_PIXELS = 1024 * 1024;

/** TouchTexture resolution per spec §4.3. */
const TOUCH_TEX_SIZE = 256;

/** Default touch decay factor per frame. */
const TOUCH_DECAY = 0.96;

/** CPU-side decay for the shader effect gate after interaction stops. */
const EFFECT_DECAY = 0.92;

interface RenderSize {
  width: number;
  height: number;
}

function clampRenderDim(value: number): number {
  return Math.max(1, Math.floor(value));
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function clampSigned(value: number): number {
  return Math.max(-1, Math.min(1, value));
}

function getEffectiveDpr(): number {
  const e2eMode =
    typeof location !== 'undefined' && new URLSearchParams(location.search).has('e2e');
  const rawDpr = typeof window !== 'undefined' ? (window.devicePixelRatio ?? 1) : 1;
  return e2eMode ? 1 : Math.min(Math.max(rawDpr, 1), 3);
}

function drawingBufferSize(renderer: THREE.WebGLRenderer): THREE.Vector2 {
  return renderer.getDrawingBufferSize(new THREE.Vector2());
}

function renderTargetSize(renderer: THREE.WebGLRenderer): RenderSize {
  const buffer = drawingBufferSize(renderer);
  let width = clampRenderDim(buffer.width * RT_SCALE);
  let height = clampRenderDim(buffer.height * RT_SCALE);
  const pixels = width * height;

  if (pixels > MAX_RT_PIXELS) {
    const scale = Math.sqrt(MAX_RT_PIXELS / pixels);
    width = clampRenderDim(width * scale);
    height = clampRenderDim(height * scale);
  }

  return { width, height };
}

function interactionEffectAmount(visual: VisualParams, touchStamps: TouchStamp[]): number {
  const gestureControl = Math.max(
    visual.feedback,
    visual.chroma,
    Math.abs(visual.zoom - 0.5) * 2,
    Math.abs(visual.rotation - 0.5) * 2,
    Math.hypot(visual.flowX, visual.flowY),
    visual.warp,
    visual.blur,
    Math.abs(visual.twist),
  );
  const touchHeat = touchStamps.reduce((max, stamp) => Math.max(max, stamp.intensity), 0);
  return clamp01(Math.max(gestureControl, touchHeat));
}

// ─── VisualEngine ───────────────────────────────────────────────────────────

export class VisualEngine {
  // ─── Private state ───────────────────────────────────────────────────────

  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.OrthographicCamera;

  /** Ping-pong pair of render targets. Index 0 = first, 1 = second. */
  private readonly pingPong: [THREE.WebGLRenderTarget, THREE.WebGLRenderTarget];

  /** Index into pingPong[] that is the "current" write target. */
  private currentRT: 0 | 1 = 0;

  /** ShaderMaterial for the feedback/accumulation pass. */
  private readonly feedbackMaterial: THREE.ShaderMaterial;

  /** ShaderMaterial for the post-processing pass (scanline, barrel, bloom). */
  private readonly postMaterial: THREE.ShaderMaterial;

  /** 256×256 heat-map texture driven by touch stamps. */
  private readonly touchTexture: TouchTexture;

  /** VideoSource instances keyed by mode index. */
  private readonly videoSources: Map<number, VideoSource> = new Map();

  /** Modes configuration. */
  private readonly modes: ModeConfig[];

  /** 0..1 gate for shader distortion. Audio alone never raises this. */
  private effectAmount = 0;

  // ─── Constructor ─────────────────────────────────────────────────────────

  constructor(opts: { canvas: HTMLCanvasElement; modes: ModeConfig[]; size?: RenderSize }) {
    const { canvas, modes, size } = opts;
    this.modes = modes;

    // ── Renderer ──────────────────────────────────────────────────────────
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false });
    const renderWidth = clampRenderDim(size?.width ?? (canvas.clientWidth || canvas.width || 1));
    const renderHeight = clampRenderDim(size?.height ?? (canvas.clientHeight || canvas.height || 1));
    this.renderer.setPixelRatio(getEffectiveDpr());
    this.renderer.setSize(renderWidth, renderHeight, false);

    // ── Scene + orthographic full-screen quad ──────────────────────────────
    this.scene = new THREE.Scene();
    // Orthographic camera for a unit quad covering NDC.
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const quad = new THREE.Mesh<THREE.PlaneGeometry, THREE.Material>(
      new THREE.PlaneGeometry(2, 2),
      new THREE.MeshBasicMaterial(), // placeholder; swapped per pass
    );
    this.scene.add(quad);

    // ── Touch texture ──────────────────────────────────────────────────────
    this.touchTexture = new TouchTexture(TOUCH_TEX_SIZE);

    // ── Ping-pong render targets ───────────────────────────────────────────
    const rtSize = renderTargetSize(this.renderer);
    this.pingPong = [
      new THREE.WebGLRenderTarget(rtSize.width, rtSize.height, { depthBuffer: false }),
      new THREE.WebGLRenderTarget(rtSize.width, rtSize.height, { depthBuffer: false }),
    ];

    // ── Feedback ShaderMaterial ────────────────────────────────────────────
    // Uniforms match feedback.frag.glsl exactly:
    // uPrev, uVideo, uTouchTex, uZoom, uRotation, uChroma, uFeedback,
    // uBass, uMid, uTreble, uRMS, uMode, uTime
    this.feedbackMaterial = new THREE.ShaderMaterial({
      vertexShader:   feedbackVert,
      fragmentShader: feedbackFrag,
      uniforms: {
        uPrev:     { value: this.pingPong[1].texture },
        uVideo:    { value: null },
        uTouchTex: { value: this.touchTexture.texture },
        uZoom:     { value: 0.5 },
        uRotation: { value: 0.5 },
        uChroma:   { value: 0.0 },
        uFeedback: { value: 0.0 },
        uEffectAmount: { value: 0.0 },
        uFlow:     { value: new THREE.Vector2(0, 0) },
        uWarp:     { value: 0.0 },
        uBlur:     { value: 0.0 },
        uTwist:    { value: 0.0 },
        uBass:     { value: 0.0 },
        uMid:      { value: 0.0 },
        uTreble:   { value: 0.0 },
        uRMS:      { value: 0.0 },
        uMode:     { value: 0 },
        uTime:     { value: 0.0 },
      },
    });

    // ── Post ShaderMaterial ────────────────────────────────────────────────
    // Uniforms match post.frag.glsl exactly:
    // uPrev, uScanAngleDeg, uScanFreq, uScanSoft, uBarrel, uBloomThresh,
    // uVignette, uResolution
    const firstMode = modes[0];
    this.postMaterial = new THREE.ShaderMaterial({
      vertexShader:   postVert,
      fragmentShader: postFrag,
      uniforms: {
        uPrev:        { value: this.pingPong[0].texture },
        uScanAngleDeg:{ value: firstMode?.scanline.angleDeg   ?? 30 },
        uScanFreq:    { value: firstMode?.scanline.freq        ?? 480 },
        uScanSoft:    { value: firstMode?.scanline.softness    ?? 0.7 },
        uBarrel:      { value: firstMode?.post.barrel          ?? 0.05 },
        uBloomThresh: { value: firstMode?.post.bloomThreshold  ?? 0.85 },
        uVignette:    { value: firstMode?.post.vignette        ?? 0.3 },
        uEffectAmount:{ value: 0.0 },
        uResolution:  { value: drawingBufferSize(this.renderer) },
      },
    });

    // Assign feedback material to the quad initially.
    quad.material = this.feedbackMaterial;
  }

  // ─── Mode management ─────────────────────────────────────────────────────

  async loadMode(index: 0 | 1 | 2): Promise<void> {
    const mode = this.modes[index];
    if (!mode) return;

    if (!this.videoSources.has(index)) {
      const vs = new VideoSource(mode.video);
      this.videoSources.set(index, vs);
      await vs.ready();
    }
  }

  setMode(index: 0 | 1 | 2): void {
    const mode = this.modes[index];
    if (!mode) return;

    // Update post-pass uniforms for the new mode's scanline/post config.
    this.postMaterial.uniforms.uScanAngleDeg.value = mode.scanline.angleDeg;
    this.postMaterial.uniforms.uScanFreq.value     = mode.scanline.freq;
    this.postMaterial.uniforms.uScanSoft.value     = mode.scanline.softness;
    this.postMaterial.uniforms.uBarrel.value       = mode.post.barrel;
    this.postMaterial.uniforms.uBloomThresh.value  = mode.post.bloomThreshold;
    this.postMaterial.uniforms.uVignette.value     = mode.post.vignette;

    // Switch video texture.
    const vs = this.videoSources.get(index);
    if (vs?.texture) {
      this.feedbackMaterial.uniforms.uVideo.value = vs.texture;
    }

    this.feedbackMaterial.uniforms.uMode.value = index;
  }

  /**
   * Best-effort start playback on every loaded VideoSource. iOS Safari accepts
   * play() reliably only inside a user gesture even when the video is muted +
   * playsinline + autoplay. main.ts calls this from the tap-to-start handler.
   * Errors are swallowed (the demo continues running on a frozen first-frame).
   */
  async playLoadedVideos(): Promise<void> {
    const tries: Promise<void>[] = [];
    for (const vs of this.videoSources.values()) {
      tries.push(vs.play().catch(() => undefined));
    }
    await Promise.all(tries);
  }

  // ─── Parameter surface ───────────────────────────────────────────────────

  setFeedback(amount: number): void {
    this.feedbackMaterial.uniforms.uFeedback.value = amount;
  }

  setZoom(amount: number): void {
    this.feedbackMaterial.uniforms.uZoom.value = amount;
  }

  setChroma(amount: number): void {
    this.feedbackMaterial.uniforms.uChroma.value = amount;
  }

  setRotation(amount: number): void {
    this.feedbackMaterial.uniforms.uRotation.value = amount;
  }

  setFlow(x: number, y: number): void {
    this.feedbackMaterial.uniforms.uFlow.value.set(clampSigned(x), clampSigned(y));
  }

  setWarp(amount: number): void {
    this.feedbackMaterial.uniforms.uWarp.value = clamp01(amount);
  }

  setBlur(amount: number): void {
    this.feedbackMaterial.uniforms.uBlur.value = clamp01(amount);
  }

  setTwist(amount: number): void {
    this.feedbackMaterial.uniforms.uTwist.value = clampSigned(amount);
  }

  stampTouch(id: number, x: number, y: number, intensity: number): void {
    // id is unused at the texture level; TouchTexture stamps by position.
    void id;
    this.touchTexture.stamp(x, y, intensity);
  }

  setAudioLevels(levels: AudioLevels): void {
    const u = this.feedbackMaterial.uniforms;
    u.uBass.value   = levels.bass;
    u.uMid.value    = levels.mid;
    u.uTreble.value = levels.treble;
    u.uRMS.value    = levels.rms;
  }

  // ─── Per-frame ───────────────────────────────────────────────────────────

  /**
   * Orchestrate all setter calls for this frame. Called by the main loop
   * before render(). Does NOT call render() — the main loop owns rAF.
   */
  apply(visual: VisualParams, levels: AudioLevels, touchStamps: TouchStamp[]): void {
    this.setFeedback(visual.feedback);
    this.setZoom(visual.zoom);
    this.setChroma(visual.chroma);
    this.setRotation(visual.rotation);
    this.setFlow(visual.flowX, visual.flowY);
    this.setWarp(visual.warp);
    this.setBlur(visual.blur);
    this.setTwist(visual.twist);
    this.setAudioLevels(levels);
    this.effectAmount = Math.max(this.effectAmount, interactionEffectAmount(visual, touchStamps));
    for (const stamp of touchStamps) {
      this.stampTouch(stamp.id, stamp.x, stamp.y, stamp.intensity);
    }
  }

  /**
   * Execute the two-pass render:
   *   1. Decay touch texture.
   *   2. Feedback pass → currentRT.
   *   3. Post pass → screen.
   *   4. Swap ping-pong.
   *
   * Called once per rAF by the main loop.
   */
  render(): void {
    this.touchTexture.decay(TOUCH_DECAY);
    this.feedbackMaterial.uniforms.uEffectAmount.value = this.effectAmount;
    this.postMaterial.uniforms.uEffectAmount.value = this.effectAmount;

    const writeRT  = this.pingPong[this.currentRT];
    const readRT   = this.pingPong[this.currentRT === 0 ? 1 : 0];

    // Update uPrev to point at the read (previous) RT texture.
    this.feedbackMaterial.uniforms.uPrev.value = readRT.texture;

    // Feedback pass → write RT.
    const quad = this.scene.children[0] as THREE.Mesh;
    quad.material = this.feedbackMaterial;
    this.renderer.setRenderTarget(writeRT);
    this.renderer.render(this.scene, this.camera);

    // Post pass → screen (null target).
    this.postMaterial.uniforms.uPrev.value = writeRT.texture;
    quad.material = this.postMaterial;
    this.renderer.setRenderTarget(null);
    this.renderer.render(this.scene, this.camera);

    // Swap: next frame's write target is the current read target.
    this.currentRT = this.currentRT === 0 ? 1 : 0;
    this.effectAmount = this.effectAmount < 0.001 ? 0 : this.effectAmount * EFFECT_DECAY;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  resize(width: number, height: number): void {
    this.renderer.setPixelRatio(getEffectiveDpr());
    this.renderer.setSize(clampRenderDim(width), clampRenderDim(height), false);

    const rtSize = renderTargetSize(this.renderer);
    this.pingPong[0].setSize(rtSize.width, rtSize.height);
    this.pingPong[1].setSize(rtSize.width, rtSize.height);

    this.postMaterial.uniforms.uResolution.value.copy(drawingBufferSize(this.renderer));
  }

  dispose(): void {
    this.pingPong[0].dispose();
    this.pingPong[1].dispose();
    this.feedbackMaterial.dispose();
    this.postMaterial.dispose();
    this.touchTexture.texture.dispose();
    for (const vs of this.videoSources.values()) {
      vs.dispose();
    }
    this.renderer.dispose();
  }
}
